import { MouseEvent, PointerEvent, useCallback, useEffect, useRef, useState } from 'react'

const DRAG_THRESHOLD = 3
const RESNAP_DELAY = 500

/**
 * Mouse-drag scrolling for a CSS scroll-snap container (touch and trackpad
 * already scroll natively). The ensuing click is suppressed after a drag so a
 * wrapping Link does not navigate when the user releases.
 */
export const useDragScroll = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const track = useRef({
    active: false,
    dragged: false,
    scrollLeft: 0,
    startX: 0,
  })
  const resnapTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(resnapTimeout.current), [])

  const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || !ref.current) {
      return
    }
    clearTimeout(resnapTimeout.current)
    track.current = {
      active: true,
      dragged: false,
      scrollLeft: ref.current.scrollLeft,
      startX: event.pageX,
    }
  }, [])

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const container = ref.current
    if (!track.current.active || !container) {
      return
    }
    const delta = event.pageX - track.current.startX
    if (!track.current.dragged && Math.abs(delta) > DRAG_THRESHOLD) {
      track.current.dragged = true
      container.setPointerCapture(event.pointerId)
      setDragging(true)
    }
    if (track.current.dragged) {
      container.scrollLeft = track.current.scrollLeft - delta
    }
  }, [])

  const onPointerUp = useCallback(() => {
    if (!track.current.active) {
      return
    }
    track.current.active = false
    if (!track.current.dragged) {
      return
    }
    const container = ref.current
    if (container && container.clientWidth) {
      container.scrollTo({
        behavior: 'smooth',
        left: Math.round(container.scrollLeft / container.clientWidth) * container.clientWidth,
      })
    }
    // keep snapping suspended until the settle animation is done, otherwise
    // re-enabling mandatory snap cuts it short with an instant jump
    resnapTimeout.current = setTimeout(() => setDragging(false), RESNAP_DELAY)
  }, [])

  const onClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!track.current.dragged) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    track.current.dragged = false
  }, [])

  return {
    dragging,
    onClick,
    onPointerCancel: onPointerUp,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    ref,
  }
}
